import React, {useEffect, useMemo, useState} from 'react';
import { Link } from 'react-router-dom';
import {
    getPreSeasonContractById,
    getPreseasonApplications,
    getFinalContractsByContractId,
    getPreSeasonContractPriceHistory,
    getPreSeasonContractQuantityHistory,
} from "../../api/web";
import moment from "moment";
import { formatMetricTonnesFromKg } from "../../utils/formatNumbers";
import { agreedPricePerKgForFinalContract, approvedQtyKgForFinalContract } from "../../utils/preseasonStats";

const TAB_KEYS = { INFO: 'info', CROP_PRICES: 'crop_prices', APPLICATIONS: 'applications', FINAL_CONTRACTS: 'final_contracts' };

const PreSeasonContractDetail = (props) => {

    const id = props.match.params.id;
    const contractId = parseInt(id, 10);
    const [activeTab, setActiveTab] = useState(TAB_KEYS.INFO);
    const [initialLoading, setInitialLoading] = useState(true);
    const [contract, setContract] = useState(null);
    const [applications, setApplications] = useState([]);
    const [applicationsLoading, setApplicationsLoading] = useState(false);
    const [finalContracts, setFinalContracts] = useState([]);
    const [finalContractsLoading, setFinalContractsLoading] = useState(false);
    const [priceHistoryPayload, setPriceHistoryPayload] = useState(null);
    const [quantityHistoryPayload, setQuantityHistoryPayload] = useState(null);
    const [contractHistoryLoading, setContractHistoryLoading] = useState(false);
    const [priceHistoryError, setPriceHistoryError] = useState(false);
    const [quantityHistoryError, setQuantityHistoryError] = useState(false);

    useEffect(() => {
        getPreSeasonContractById(id).then((response) => {
            if (!response.error) {
                setContract(response.payload);
            }
            setInitialLoading(false);
        });
    }, [id]);

    useEffect(() => {
        if (!contract || !contractId) return;
        setApplicationsLoading(true);
        getPreseasonApplications().then((res) => {
            if (!res.error && Array.isArray(res.payload)) {
                const forContract = res.payload.filter(
                    (app) => (app.contract?.id ?? app.contract_id ?? app.contract) === contractId
                );
                setApplications(forContract);
            } else {
                setApplications([]);
            }
            setApplicationsLoading(false);
        });
    }, [contract, contractId]);

    useEffect(() => {
        if (!contract || !contractId) return;
        setFinalContractsLoading(true);
        getFinalContractsByContractId(contractId).then((res) => {
            if (!res.error && Array.isArray(res.payload)) {
                setFinalContracts(res.payload);
            } else {
                setFinalContracts([]);
            }
            setFinalContractsLoading(false);
        });
    }, [contract, contractId]);

    useEffect(() => {
        if (!contract?.id || contract.id !== contractId) return undefined;
        const cid = contractId;
        let alive = true;
        setContractHistoryLoading(true);
        setPriceHistoryError(false);
        setQuantityHistoryError(false);
        setPriceHistoryPayload(null);
        setQuantityHistoryPayload(null);
        Promise.all([
            getPreSeasonContractPriceHistory(cid),
            getPreSeasonContractQuantityHistory(cid),
        ])
            .then(([pr, qh]) => {
                if (!alive) return;
                setPriceHistoryPayload(pr.error ? null : pr.payload);
                setQuantityHistoryPayload(qh.error ? null : qh.payload);
                setPriceHistoryError(!!pr.error);
                setQuantityHistoryError(!!qh.error);
            })
            .finally(() => {
                if (alive) setContractHistoryLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [contract, contractId]);

    const applicationsById = useMemo(() => {
        const m = new Map();
        for (const app of applications) {
            if (app?.id != null) m.set(app.id, app);
        }
        return m;
    }, [applications]);

    const statusBadge = (status) => {
        const map = {
            draft: { cls: 'bg-secondary text-white', style: {} },
            open: { cls: 'bg-success text-white', style: {} },
            closed: { cls: 'bg-dark text-white', style: {} }
        };
        const c = map[status] || { cls: '', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    const applicationStatusBadge = (status) => {
        const map = {
            pending: { cls: 'text-white', style: { backgroundColor: '#fd7e14' } },
            revision: { cls: 'bg-warning text-dark', style: {} },
            approved: { cls: 'bg-success text-white', style: {} },
            rejected: { cls: 'bg-danger text-white', style: {} }
        };
        const c = map[status] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    const finalContractStatusBadge = (status) => {
        const map = {
            awaiting_farmer: { cls: 'text-white', style: { backgroundColor: '#fd7e14' } },
            finalized: { cls: 'bg-success text-white', style: {} }
        };
        const c = map[status] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    if (initialLoading) {
        return (
            <div className="content-body">
                <div className="container d-flex justify-content-center ht-100p">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mg-t-10">Loading preseason contract</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!contract) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="mg-b-20">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/preseason_contract">Preseason Contracts</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Not found</li>
                            </ol>
                        </nav>
                        <p className="tx-danger">Preseason contract not found.</p>
                        <Link to="/preseason_contract" className="btn btn-sm btn-primary">Back to Preseason Contracts</Link>
                    </div>
                </div>
            </div>
        );
    }

    const cropPrices = contract.crop_prices || [];

    const farmerDisplay = (app) => {
        if (!app.farmer) return '—';
        if (typeof app.farmer === 'object') {
            const n = [app.farmer.first_name, app.farmer.last_name].filter(Boolean).join(' ');
            return n || app.farmer.username || app.farmer.id || '—';
        }
        return `#${app.farmer}`;
    };
    const cropDisplay = (app) => {
        if (app.crop && typeof app.crop === 'object') return app.crop.name ?? app.crop.id ?? '—';
        if (app.crop != null) return `#${app.crop}`;
        return '—';
    };

    const resolvedApplicationForFinal = (fc) => {
        const aid = fc?.application?.id;
        if (aid != null && applicationsById.has(aid)) return applicationsById.get(aid);
        return null;
    };

    const farmerNameForFinal = (fc) => {
        const f = fc?.snapshot_json?.application?.farmer;
        if (f && typeof f === 'object') {
            const n = [f.first_name, f.last_name].filter(Boolean).join(' ');
            if (n || f.username) return n || f.username;
            if (f.id != null) return `Farmer #${f.id}`;
        }
        const app = resolvedApplicationForFinal(fc);
        if (app) return farmerDisplay(app);
        const bid = fc?.application?.farmer;
        return bid != null ? `Farmer #${bid}` : '—';
    };

    const cropNameForFinal = (fc) => {
        const name = fc?.snapshot_json?.application?.crop_name;
        if (name) return name;
        const app = resolvedApplicationForFinal(fc);
        if (app) return cropDisplay(app);
        const cropId = fc?.application?.crop;
        const rows = contract.crop_prices || [];
        const matched = rows.find((r) => {
            const cid = typeof r?.crop === 'object' ? r?.crop?.id : r?.crop;
            return cid === cropId || String(cid) === String(cropId);
        });
        if (matched?.crop_name) return matched.crop_name;
        return cropId != null ? `Crop #${cropId}` : '—';
    };

    const formatMoneyCell = (n) => (n > 0 ? Number(n).toLocaleString() : '—');

    const formatPriceCell = (v) => {
        if (v == null || v === '') return '—';
        const n = Number(v);
        return Number.isFinite(n) ? n.toLocaleString() : String(v);
    };

    const listPriceHistoryEntries = Array.isArray(priceHistoryPayload?.entries)
        ? priceHistoryPayload.entries
        : [];
    const quantityHistoryEntries = Array.isArray(quantityHistoryPayload?.entries)
        ? quantityHistoryPayload.entries
        : [];

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/preseason_contract">Preseason Contracts</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Contract #{contract.id}</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-5 tx-spacing--1">Preseason Contract #{contract.id}</h4>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="tx-color-03 tx-13">Company: {contract.company_pacra_id ?? contract.company ?? '—'}</span>
                            {statusBadge(contract.status)}
                        </div>
                    </div>
                    <div className="d-none d-md-block">
                        <Link to="/preseason_contract" className="btn btn-sm pd-x-15 btn-secondary btn-uppercase">
                            <i className="wd-10 mg-r-5 fa fa-arrow-left"></i> Back to list
                        </Link>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="nav-wrapper mg-b-20 tx-13">
                            <ul className="nav nav-line nav-fill tx-medium">
                                <li className="nav-item">
                                    <a href="#" className={`nav-link ${activeTab === TAB_KEYS.INFO ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab(TAB_KEYS.INFO); }}>
                                        <i className="ion-md-information-circle"/> Information
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#" className={`nav-link ${activeTab === TAB_KEYS.CROP_PRICES ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab(TAB_KEYS.CROP_PRICES); }}>
                                        <i className="ion-md-pricetag"/> Crop Prices ({cropPrices.length})
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#" className={`nav-link ${activeTab === TAB_KEYS.APPLICATIONS ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab(TAB_KEYS.APPLICATIONS); }}>
                                        <i className="ion-md-document"/> Applications ({applications.length})
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#" className={`nav-link ${activeTab === TAB_KEYS.FINAL_CONTRACTS ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab(TAB_KEYS.FINAL_CONTRACTS); }}>
                                        <i className="ion-md-checkmark-done"/> Final Contracts ({finalContracts.length})
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="tab-content">
                            <div className={`tab-pane fade ${activeTab === TAB_KEYS.INFO ? 'active show' : ''}`}>
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row row-sm">
                                            <div className="col-md-6">
                                                <p><strong>Company (Pacra ID):</strong> {contract.company_pacra_id ?? contract.company ?? '—'}</p>
                                                <p><strong>Status:</strong> {statusBadge(contract.status)}</p>
                                                <p><strong>Total quantity (t):</strong> {formatMetricTonnesFromKg(contract.total_quantity_kg)}</p>
                                                <p><strong>Delivery location:</strong> {contract.delivery_location ?? '—'}</p>
                                                <p><strong>Delivery date:</strong> {contract.delivery_date ? moment(contract.delivery_date).format("Do MMM, YYYY") : '—'}</p>
                                                <p><strong>Application deadline:</strong> {contract.application_deadline ? moment(contract.application_deadline).format("Do MMM, YYYY") : '—'}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p><strong>Min per farmer (t):</strong> {formatMetricTonnesFromKg(contract.min_per_farmer_kg)}</p>
                                                <p><strong>Max per farmer (t):</strong> {formatMetricTonnesFromKg(contract.max_per_farmer_kg)}</p>
                                                <p><strong>Province:</strong> {contract.province ?? (typeof contract.province === 'object' ? contract.province?.name : '—')}</p>
                                                <p><strong>Town:</strong> {contract.town ?? (typeof contract.town === 'object' ? contract.town?.name : '—')}</p>
                                                <p><strong>Date created:</strong> {contract.date_created ? moment(contract.date_created).format("Do MMM, YYYY HH:mm") : '—'}</p>
                                                {contract.terms ? (
                                                    <p><strong>Terms:</strong><br/><span className="tx-color-03">{contract.terms}</span></p>
                                                ) : null}
                                            </div>
                                        </div>
                                        {quantityHistoryError ? (
                                            <p className="tx-danger tx-13 mg-b-0 mg-t-15">Quantity history could not be loaded. Refresh the page to try again.</p>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="card mg-t-20">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Quantity history</h6>
                                        <p className="tx-12 tx-color-03 mg-b-0 mg-t-5">Changes to the contract total quantity (capacity).</p>
                                    </div>
                                    <div className="card-body">
                                        {contractHistoryLoading ? (
                                            <div className="d-flex align-items-center pd-y-10">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading quantity history…</span>
                                            </div>
                                        ) : quantityHistoryEntries.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No quantity change records yet.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm mg-b-0">
                                                    <thead>
                                                        <tr>
                                                            <th>When</th>
                                                            <th>Previous total (t)</th>
                                                            <th>New total (t)</th>
                                                            <th>Recorded by</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {quantityHistoryEntries.map((entry) => (
                                                            <tr key={entry.id}>
                                                                <td>{entry.created_at ? moment(entry.created_at).format('Do MMM, YYYY HH:mm') : '—'}</td>
                                                                <td>{entry.previous_total_quantity_kg != null ? formatMetricTonnesFromKg(entry.previous_total_quantity_kg) : '—'}</td>
                                                                <td>{formatMetricTonnesFromKg(entry.new_total_quantity_kg)}</td>
                                                                <td>{entry.created_by_username ?? '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={`tab-pane fade ${activeTab === TAB_KEYS.CROP_PRICES ? 'active show' : ''}`}>
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Crop Prices</h6>
                                    </div>
                                    <div className="card-body">
                                        {cropPrices.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No crop prices defined.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Crop</th>
                                                            <th>Price per kg</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cropPrices.map((item, idx) => (
                                                            <tr key={item.id || idx}>
                                                                <td>{item.crop_name ?? item.crop ?? '—'}</td>
                                                                <td>{item.price_per_kg != null ? Number(item.price_per_kg).toLocaleString() : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="card mg-t-20">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">List price history</h6>
                                        <p className="tx-12 tx-color-03 mg-b-0 mg-t-5">
                                            Changes to this contract&apos;s advertised crop prices (create and edits).
                                        </p>
                                    </div>
                                    <div className="card-body">
                                        {priceHistoryError ? (
                                            <p className="tx-danger tx-13 mg-b-0">Price history could not be loaded. Refresh the page to try again.</p>
                                        ) : contractHistoryLoading ? (
                                            <div className="d-flex align-items-center pd-y-10">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading price history…</span>
                                            </div>
                                        ) : listPriceHistoryEntries.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No list price changes recorded for this contract yet.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm mg-b-0">
                                                    <thead>
                                                        <tr>
                                                            <th>When</th>
                                                            <th>Crop</th>
                                                            <th>Previous price / kg</th>
                                                            <th>New price / kg</th>
                                                            <th>Recorded by</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {listPriceHistoryEntries.map((entry) => (
                                                            <tr key={entry.id}>
                                                                <td>{entry.created_at ? moment(entry.created_at).format('Do MMM, YYYY HH:mm') : '—'}</td>
                                                                <td>{entry.crop_name ?? '—'}</td>
                                                                <td>{formatPriceCell(entry.previous_price_per_kg)}</td>
                                                                <td>{formatPriceCell(entry.new_price_per_kg)}</td>
                                                                <td>{entry.created_by_username ?? '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={`tab-pane fade ${activeTab === TAB_KEYS.APPLICATIONS ? 'active show' : ''}`}>
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Preseason Applications</h6>
                                    </div>
                                    <div className="card-body">
                                        {applicationsLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading applications...</span>
                                            </div>
                                        ) : applications.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No applications for this contract.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Farmer</th>
                                                            <th>Crop</th>
                                                            <th>Requested (t)</th>
                                                            <th>Approved (t)</th>
                                                            <th>Status</th>
                                                            <th>Preferred delivery</th>
                                                            <th>Created</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {applications.map((app) => (
                                                            <tr key={app.id}>
                                                                <td>{farmerDisplay(app)}</td>
                                                                <td>{cropDisplay(app)}</td>
                                                                <td>{formatMetricTonnesFromKg(app.requested_qty_kg)}</td>
                                                                <td>{formatMetricTonnesFromKg(app.approved_qty_kg)}</td>
                                                                <td>{applicationStatusBadge(app.status)}</td>
                                                                <td>{app.preferred_delivery_date ? moment(app.preferred_delivery_date).format("Do MMM, YYYY") : '—'}</td>
                                                                <td>{app.created_at ? moment(app.created_at).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={`tab-pane fade ${activeTab === TAB_KEYS.FINAL_CONTRACTS ? 'active show' : ''}`}>
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Final Contracts</h6>
                                    </div>
                                    <div className="card-body">
                                        {finalContractsLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="mg-l-10 tx-13">Loading final contracts...</span>
                                            </div>
                                        ) : finalContracts.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No final contracts for this preseason contract.</p>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Application</th>
                                                            <th>Farmer</th>
                                                            <th>Crop</th>
                                                            <th>Approved (t)</th>
                                                            <th>Price / kg</th>
                                                            <th>Contract value (ZMW)</th>
                                                            <th>Status</th>
                                                            <th>Company signed</th>
                                                            <th>Farmer confirmed</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {finalContracts.map((fc) => {
                                                            const priceKg = agreedPricePerKgForFinalContract(fc);
                                                            const qtyKg = approvedQtyKgForFinalContract(fc);
                                                            const value = priceKg > 0 ? qtyKg * priceKg : 0;
                                                            return (
                                                            <tr key={fc.id}>
                                                                <td>#{fc.application?.id ?? '—'}</td>
                                                                <td>{farmerNameForFinal(fc)}</td>
                                                                <td>{cropNameForFinal(fc)}</td>
                                                                <td>{formatMetricTonnesFromKg(approvedQtyKgForFinalContract(fc))}</td>
                                                                <td>{formatMoneyCell(priceKg)}</td>
                                                                <td>{formatMoneyCell(value)}</td>
                                                                <td>{finalContractStatusBadge(fc.status)}</td>
                                                                <td>{fc.company_signed_at ? moment(fc.company_signed_at).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                                <td>{fc.farmer_confirmed_at ? moment(fc.farmer_confirmed_at).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                            </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreSeasonContractDetail;
